import React from 'react';
import { ShieldCheck, Smartphone, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Footer.css';

const Footer = ({ isDark, lang }) => {
    const currentYear = new Date().getFullYear();

    return (
        <footer className={`footer-main px-4 sm:px-6 pb-12 transition-colors duration-500 ${isDark ? 'dark' : 'light'}`}>
            <div className="container mx-auto max-w-7xl">

                <div className={`footer-glass-card p-8 sm:p-12 md:p-14 rounded-[2.5rem] sm:rounded-[3.5rem] border backdrop-blur-3xl transition-all duration-700 
                    ${isDark
                        ? 'bg-slate-900/60 border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.4)]'
                        : 'bg-white border-slate-200 shadow-[0_40px_100px_rgba(59,130,246,0.06)]'}`}>

                    {/* GRID PRINCIPAL SIN SECCIÓN DE CONTACTO */}
                    <div className="footer-grid-wrapper grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 text-left">

                        {/* COLUMNA 1: IDENTIDAD (Icono original sin invertir color) */}
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-3.5">
                                <img 
                                    src="/logo192.png" 
                                    alt="Logo TDSMX" 
                                    className="w-10 h-10 object-contain" 
                                />
                                <div className="flex flex-col">
                                    <span className={`font-black text-xl tracking-tighter uppercase ${isDark ? 'text-white' : 'text-slate-950'}`}>
                                        TDSMX
                                    </span>
                                </div>
                            </div>
                            <p className="text-xs font-bold opacity-60 leading-relaxed max-w-xs">
                                {lang === 'es' 
                                    ? 'Software móvil independiente diseñado para resolver necesidades reales: seguridad cívica, agilidad laboral y comercio tradicional.'
                                    : 'Independent mobile software crafted to address real needs: civic safety, hiring agility, and neighborhood trade.'}
                            </p>
                        </div>

                        {/* COLUMNA 2: PLATAFORMAS */}
                        <div className="flex flex-col gap-4">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500">
                                {lang === 'es' ? 'Iniciativas' : 'Platforms'}
                            </h4>
                            <nav className="flex flex-col gap-2.5 font-bold text-xs sm:text-sm">
                                <Link to="/proyectos/kanan" className={`transition-all hover:translate-x-1 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-700 hover:text-blue-600'}`}>
                                    Kanan <span className="opacity-40 text-[10px] uppercase ml-1">// Seguridad & Red Cívica</span>
                                </Link>
                                <Link to="/proyectos/Blebe" className={`transition-all hover:translate-x-1 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-700 hover:text-blue-600'}`}>
                                    Blebe <span className="opacity-40 text-[10px] uppercase ml-1">// Apoyo en Selección</span>
                                </Link>
                                <Link to="/proyectos/Marchantite" className={`transition-all hover:translate-x-1 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-700 hover:text-blue-600'}`}>
                                    Marchantite <span className="opacity-40 text-[10px] uppercase ml-1">// Mercados Locales</span>
                                </Link>
                                <Link to="/proyectos/Niya" className={`transition-all hover:translate-x-1 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-700 hover:text-blue-600'}`}>
                                    Niya <span className="opacity-40 text-[10px] uppercase ml-1">// Salud Financiera</span>
                                </Link>
                            </nav>
                        </div>

                        {/* COLUMNA 3: LA FIRMA */}
                        <div className="flex flex-col gap-4">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-500">
                                {lang === 'es' ? 'La Firma' : 'The Firm'}
                            </h4>
                            <nav className="flex flex-col gap-2.5 font-bold text-xs sm:text-sm">
                                <Link to="/" className={`transition-all hover:translate-x-1 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-700 hover:text-blue-600'}`}>
                                    {lang === 'es' ? 'Inicio' : 'Home'}
                                </Link>
                                <Link to="/nosotros" className={`transition-all hover:translate-x-1 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-700 hover:text-blue-600'}`}>
                                    {lang === 'es' ? 'Nosotros' : 'About us'}
                                </Link>
                                <a href="/#genesis" className={`transition-all hover:translate-x-1 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-700 hover:text-blue-600'}`}>
                                    {lang === 'es' ? 'Historia' : 'History'}
                                </a>
                            </nav>
                        </div>

                        {/* COLUMNA 4: CRITERIO OPERATIVO */}
                        <div className="flex flex-col gap-4">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500">
                                {lang === 'es' ? 'Criterio Operativo' : 'Core Focus'}
                            </h4>
                            <div className={`p-5 rounded-[2rem] border transition-all ${isDark ? 'bg-black/30 border-white/5' : 'bg-blue-50/50 border-blue-100'}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="relative flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                                    </div>
                                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-500">
                                        {lang === 'es' ? 'Ecosistema en Marcha' : 'Active Ecosystem'}
                                    </span>
                                </div>
                                <div className={`space-y-1.5 text-[10px] font-bold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    <p className="flex items-center gap-1.5"><Smartphone size={12} className="text-blue-400" /> Mobile-First</p>
                                    <p className="flex items-center gap-1.5"><ShieldCheck size={12} className="text-indigo-400" /> Privacidad & Respaldo</p>
                                    <p className="flex items-center gap-1.5"><HeartHandshake size={12} className="text-emerald-400" /> Impacto Social Directo</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* CRÉDITOS INFERIORES */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-10 px-6 opacity-40 gap-4 text-center sm:text-left">
                    <p className={`text-[9px] font-black uppercase tracking-[0.25em] ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        © {currentYear} TDSMX — {lang === 'es' ? 'Todos los derechos reservados' : 'All rights reserved'}
                    </p>
                    <div className={`flex items-center gap-6 text-[9px] font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        <span className="flex items-center gap-1.5">
                            <ShieldCheck size={12} /> {lang === 'es' ? 'Desarrollo Independiente' : 'Independent Software'}
                        </span>
                        <span>{lang === 'es' ? 'Privacidad & Ética' : 'Privacy & Ethics'}</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;